/**
 * Kopfzeile eines Channel-Akkordeons in Wizard-Schritt 2. Offen: Feld-Zaehler,
 * Hinweis-Badge und "Alle auswaehlen". Zugeklappt: Kurzfassung der gewaehlten
 * Felder plus Credit-Schaetzung, damit man ohne Aufklappen sieht was laeuft.
 */

import { ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/v2/ui'

interface ChannelFieldAccordionHeaderProps {
  channelId: string
  channelLabel: string
  open: boolean
  selectedCount: number
  totalCount: number
  warningCount: number
  selectedLabels: string[]
  credits: number | null
  onToggle: () => void
  onSelectAll: () => void
}

export function ChannelFieldAccordionHeader({
  channelId,
  channelLabel,
  open,
  selectedCount,
  totalCount,
  warningCount,
  selectedLabels,
  credits,
  onToggle,
  onSelectAll,
}: ChannelFieldAccordionHeaderProps) {
  const { t } = useTranslation()
  const Chevron = open ? ChevronDown : ChevronRight

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <button
        type="button"
        id={`text-bulk-wizard-channel-${channelId}-toggle`}
        onClick={onToggle}
        aria-expanded={open}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <Chevron className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden="true" />
        <span className="truncate font-medium text-neutral-900 dark:text-neutral-100">
          {channelLabel}
        </span>
        <Badge variant="primary">
          {t('channelContent.wizard.fieldsOfTotal', { selected: selectedCount, total: totalCount })}
        </Badge>
        {warningCount > 0 && (
          <Badge variant="warning">
            {t('channelContent.wizard.hintCount', { count: warningCount })}
          </Badge>
        )}
        {!open && selectedLabels.length > 0 && (
          <span className="truncate text-sm text-neutral-500 dark:text-neutral-400">
            {selectedLabels.join(' · ')}
          </span>
        )}
      </button>

      {open ? (
        <button
          type="button"
          id={`text-bulk-wizard-channel-${channelId}-select-all`}
          onClick={onSelectAll}
          className="shrink-0 text-sm font-medium text-primary-500 hover:text-primary-600"
        >
          {t('channelContent.wizard.selectAllFields')}
        </button>
      ) : (
        <div className="flex shrink-0 items-center gap-4">
          {credits !== null && (
            <span className="text-sm text-neutral-500 dark:text-neutral-400">
              {t('channelContent.wizard.approxCredits', { count: credits })}
            </span>
          )}
          <button
            type="button"
            id={`text-bulk-wizard-channel-${channelId}-edit`}
            onClick={onToggle}
            className="text-sm font-medium text-primary-500 hover:text-primary-600"
          >
            {t('channelContent.wizard.editChannel')}
          </button>
        </div>
      )}
    </div>
  )
}
